/**
 * useTeamRoster — fetches the first team and its members.
 *
 * Phase 2 assumption: Sherlock manages a single team per Detect org.
 * The hook pulls GET /api/teams/ to find the first active team, then
 * GET /api/teams/{id}/members/ for its roster. Both are refetched together
 * via `refetch`.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError, teams as teamsApi } from '@/helpers/ApiHelper';
import type { Team, TeamMember } from '@/types/team';

interface UseTeamRosterResult {
  team: Team | null;
  members: TeamMember[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useTeamRoster(): UseTeamRosterResult {
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => {
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const teamPage = await teamsApi.listTeams();
        if (cancelled) return;

        const firstTeam = teamPage.results[0] ?? null;
        setTeam(firstTeam);

        if (firstTeam) {
          const memberPage = await teamsApi.listMembers(firstTeam.id);
          if (cancelled) return;
          setMembers(memberPage.results);
        } else {
          setMembers([]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.detail : 'Failed to load team roster');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [tick]);

  return { team, members, isLoading, error, refetch };
}
